import { ACCESS_DENIED_PATH } from '@gestion-permisos/contracts';
import { Link, Navigate } from 'react-router-dom';
import { ApiProblem } from '../api/problem.js';
import { ProblemMessage } from './problem-message.js';

/**
 * Error de la API dentro de una pantalla autenticada (plan 11 §4.4 y W1):
 * - 403: acceso denegado, aunque la navegación hubiera ofrecido el enlace (la navegación no es seguridad).
 *   Si es `NO_ACTIVE_EMPLOYEE`, la sesión ya no sirve: pantalla pública de acceso denegado.
 * - 404: «no encontrada» (recurso inexistente o fuera de ámbito), distinta de una ruta desconocida.
 * - 401: el cliente ya llevó a la pantalla de sesión expirada.
 * - Cualquier otro: el Problem Details (`title`, `detail` y `traceId`), sin SQL ni trazas.
 */
export function ApiErrorView({ error }: { error: unknown }) {
  if (error instanceof ApiProblem) {
    if (error.status === 401) return null;
    if (error.status === 403 && error.code === 'NO_ACTIVE_EMPLOYEE') {
      return <Navigate to={`${ACCESS_DENIED_PATH}?code=NO_ACTIVE_EMPLOYEE`} replace />;
    }
    if (error.status === 403) {
      return (
        <ErrorSection title="Acceso denegado" traceId={error.traceId}>
          No tienes permiso para ver este contenido.
        </ErrorSection>
      );
    }
    if (error.status === 404) {
      return (
        <ErrorSection title="No encontrada" traceId={error.traceId}>
          El recurso no existe o no está disponible para ti.
        </ErrorSection>
      );
    }
  }
  return <ProblemMessage error={error} />;
}

function ErrorSection({ title, traceId, children }: { title: string; traceId: string | undefined; children: string }) {
  return (
    <section>
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="mt-2 text-muted-foreground">{children}</p>
      {traceId ? <p className="mt-2 text-xs text-muted-foreground">Referencia para soporte: {traceId}</p> : null}
      <Link to="/" className="mt-4 inline-block text-primary underline underline-offset-4">
        Ir al inicio
      </Link>
    </section>
  );
}
